import React, { Component } from "react";
import {
  withModulesManager,
  withHistory,
  formatMessage,
  formatDateFromISO,
} from "@openimis/fe-core";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  Typography,
  Paper,
  Box,
  Grid,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
} from "@material-ui/core";
import { ArrowBack as BackIcon } from "@material-ui/icons";
import { withStyles } from "@material-ui/core/styles";
import {
  fetchPolicyHolderEnrollmentBatch,
  submitPolicyHolderEnrollmentBatch,
  reviewPolicyHolderEnrollmentBatch,
  approvePolicyHolderEnrollmentBatch,
  rejectPolicyHolderEnrollmentBatch,
  turnToDraftPolicyHolderEnrollmentBatch,
  deletePolicyHolderEnrollmentBatch,
} from "../../actions";
import EnrollmentStatusBadge from "../../components/common/EnrollmentStatusBadge";
import EnrollmentActionButtons from "../../components/common/EnrollmentActionButtons";
import KeyValueSection from "../../components/common/KeyValueSection";
import {
  enrollmentMetaRows,
  normalizeInsureeData,
  formatDisplayValue,
  buildFieldRows,
  normalizePolicyholderData,
} from "../../utils/enrollmentData";

const styles = (theme) => ({
  page: { margin: theme.spacing(2) },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2),
  },
  titleWrap: { display: "flex", alignItems: "center", gap: theme.spacing(2), flex: 1 },
  title: { fontWeight: 700, color: theme.palette.primary.main },
  card: { marginBottom: theme.spacing(2) },
});

class PolicyHolderEnrollmentBatchDetail extends Component {
  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.match?.params?.enrollmentId;
    const nextId = this.props.match?.params?.enrollmentId;
    if (prevId !== nextId) this.load();
  }

  load = () => {
    const uuid = this.props.match?.params?.enrollmentId;
    if (uuid) {
      this.props.fetchPolicyHolderEnrollmentBatch(this.props.modulesManager, uuid);
    }
  };

  handleBack = () => {
    const { history } = this.props;
    if (history) history.push("/enrollment/policyholder-batch");
    else window.location.href = "/enrollment/policyholder-batch";
  };

  openEmployee = (uuid) => {
    if (this.props.history && uuid) {
      this.props.history.push(`/enrollment/insuree/${uuid}/detail`);
    }
  };

  openPolicyholderEnrollment = (uuid) => {
    if (this.props.history && uuid) {
      this.props.history.push(`/enrollment/policyholder/${uuid}/detail`);
    }
  };

  run = (action, payload) => {
    const mm = this.props.modulesManager;
    return Promise.resolve(action(mm, payload)).then(() => this.load());
  };

  render() {
    const { classes, intl, modulesManager, enrollment, loading } = this.props;
    if (loading && !enrollment) {
      return (
        <Box className={classes.page} display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      );
    }
    if (!enrollment) {
      return (
        <Box className={classes.page}>
          <Typography>Batch not found.</Typography>
          <IconButton onClick={this.handleBack}>
            <BackIcon />
          </IconButton>
        </Box>
      );
    }

    const phData = normalizePolicyholderData(
      enrollment.policyholderEnrollment?.policyholderData
    );
    const batchRows = buildFieldRows([
      ["Policy holder", enrollment.policyHolder?.tradeName],
      ["Policy holder code", enrollment.policyHolder?.code],
      ["Linked PH enrollment", enrollment.policyholderEnrollment?.uuid],
      ["PH enrollment status", enrollment.policyholderEnrollment?.status],
      ["PH name (from enrollment)", phData.name],
      ["Employee count", enrollment.employeeCount],
    ]);
    const metaRows = enrollmentMetaRows(enrollment);
    const employees = enrollment.employeeEnrollments || [];

    return (
      <Box className={classes.page}>
        <Box className={classes.header}>
          <Box className={classes.titleWrap}>
            <IconButton onClick={this.handleBack}>
              <BackIcon />
            </IconButton>
            <Typography variant="h5" className={classes.title}>
              {formatMessage(intl, "enrollment", "policyHolderBatch.detail") ||
                "Policy Holder Enrollment Batch"}
            </Typography>
            <EnrollmentStatusBadge status={enrollment.status} />
          </Box>
        </Box>

        <Card className={classes.card}>
          <CardContent>
            <EnrollmentActionButtons
              intl={intl}
              enrollment={enrollment}
              onSubmit={(p) =>
                this.run(this.props.submitPolicyHolderEnrollmentBatch, p)
              }
              onReview={(p) =>
                this.run(this.props.reviewPolicyHolderEnrollmentBatch, p)
              }
              onApprove={(p) =>
                this.run(this.props.approvePolicyHolderEnrollmentBatch, p)
              }
              onReject={(p) =>
                this.run(this.props.rejectPolicyHolderEnrollmentBatch, p)
              }
              onTurnToDraft={(p) =>
                this.run(this.props.turnToDraftPolicyHolderEnrollmentBatch, p)
              }
              onDelete={(uuid) =>
                this.run(this.props.deletePolicyHolderEnrollmentBatch, uuid).then(
                  this.handleBack
                )
              }
            />
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Batch details" rows={batchRows} />
                {enrollment.policyholderEnrollment?.uuid && (
                  <Box mt={1}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() =>
                        this.openPolicyholderEnrollment(
                          enrollment.policyholderEnrollment.uuid
                        )
                      }
                    >
                      Open linked policy holder enrollment
                    </Link>
                  </Box>
                )}
              </Box>
            </Paper>
            <Paper className={classes.card}>
              <Box p={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Employee enrollments
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CHF ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>No employees</TableCell>
                      </TableRow>
                    )}
                    {employees.map((emp) => {
                      const d = normalizeInsureeData(emp.insureeData);
                      return (
                        <TableRow
                          key={emp.uuid}
                          hover
                          style={{ cursor: "pointer" }}
                          onClick={() => this.openEmployee(emp.uuid)}
                        >
                          <TableCell>{formatDisplayValue(d.chfId)}</TableCell>
                          <TableCell>
                            {formatDisplayValue(d.lastName)},{" "}
                            {formatDisplayValue(d.otherNames)}
                          </TableCell>
                          <TableCell>
                            <EnrollmentStatusBadge status={emp.status} size="small" />
                          </TableCell>
                          <TableCell>
                            {formatDateFromISO(
                              modulesManager,
                              intl,
                              emp.dateCreated
                            ) || "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper className={classes.card}>
              <Box p={2}>
                <KeyValueSection title="Enrollment info" rows={metaRows} />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  }
}

const mapStateToProps = (state) => ({
  enrollment: state.enrollment?.policyHolderBatchDetail,
  loading: state.enrollment?.fetchingPolicyHolderBatches,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPolicyHolderEnrollmentBatch,
      submitPolicyHolderEnrollmentBatch,
      reviewPolicyHolderEnrollmentBatch,
      approvePolicyHolderEnrollmentBatch,
      rejectPolicyHolderEnrollmentBatch,
      turnToDraftPolicyHolderEnrollmentBatch,
      deletePolicyHolderEnrollmentBatch,
    },
    dispatch
  );

export default withModulesManager(
  withHistory(
    injectIntl(
      withStyles(styles)(
        connect(
          mapStateToProps,
          mapDispatchToProps
        )(PolicyHolderEnrollmentBatchDetail)
      )
    )
  )
);
